import React, { ReactNode } from 'react';
import { IconInfoRegular, LogoFastship } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';

import { Typography } from '@common/styles';
import { displayPrice } from '@common/utils';
import { EstimatedShippingDate, ShippingSendDate, TouchAccessibilityWrapper } from '@domains/pdp-common';

import { GuideText } from './guide/GuideText';
import { GeneralExtraInfo, ShippingDetails } from './GeneralExtraInfo';

export interface GeneralProductProps {
  shipping_details: ShippingDetails;
  estimated_shipping_date: EstimatedShippingDate;
  shipping_send_date: ShippingSendDate | null;
  show_free_shipping_amount: boolean;
  is_over_max_days: boolean;
  isFastShipping: boolean;
  fast_guide_text: string | null;
  item_guide_text: string | null;
  type_text: string;
  isOverseasPurchase: boolean;
  getShippingFeeText: (shipping_fee: number | null) => string;
  handleInfoIconClick: (children: ReactNode) => void;
}

export const GeneralProduct: React.FC<React.PropsWithChildren<GeneralProductProps>> = ({
  shipping_details,
  estimated_shipping_date,
  shipping_send_date,
  show_free_shipping_amount,
  is_over_max_days,
  isFastShipping,
  fast_guide_text,
  item_guide_text,
  type_text,
  isOverseasPurchase,
  getShippingFeeText,
  handleInfoIconClick,
}) => {
  const language = 'ko';
  const queryClient = useQueryClient();

  return (
    <ShippingInfo>
      <ShippingInfoContent>
        <ShippingInfoTitle className={Typography.BODY_14_MEDIUM}>배송</ShippingInfoTitle>
        <div>
          <ShippingInfoList className={Typography.BODY_14_SEMIBOLD}>
            {isOverseasPurchase && '해외배송'}
            {!isOverseasPurchase && (isFastShipping ? <LogoFastship width={67} height={13} /> : '일반배송')}
            <Divider />

            <ShippingFeeInfo className={Typography.BODY_14_REGULAR} dotDivider={isFastShipping}>
              <span>{getShippingFeeText(shipping_details.shipping_fee)}</span>
              <TouchAccessibilityWrapper
                touchWidth={30}
                touchHeight={30}
                onClick={() =>
                  handleInfoIconClick(
                    <QueryClientProvider client={queryClient}>
                      {shipping_details && (
                        <InfoContainer>
                          <InfoTitle>{'배송정보'}</InfoTitle>
                          <GeneralExtraInfo
                            shipping_details={shipping_details}
                            shipping_estimate_info={estimated_shipping_date.estimate_list}
                          />
                        </InfoContainer>
                      )}
                    </QueryClientProvider>,
                  )
                }
                data-allow-preview={true}
              >
                <Area>
                  {show_free_shipping_amount && (
                    <FreeShippingAmount className={Typography.BODY_14_REGULAR}>
                      <span>&nbsp;·&nbsp;</span>
                      {`${displayPrice(shipping_details.free_shipping_amount ?? 0)} 이상 무료`}
                    </FreeShippingAmount>
                  )}
                  <IconInfoRegular width={20} height={20} color='textTertiary' />
                </Area>
              </TouchAccessibilityWrapper>
            </ShippingFeeInfo>
          </ShippingInfoList>
          {estimated_shipping_date.repr_estimate && (
            <EstimateInfo>
              <strong>{estimated_shipping_date.repr_estimate.day}일</strong>{' '}
              {is_over_max_days ? '이후 발송될 확률' : '이내 출발 확률'}{' '}
              <b>({Math.floor(estimated_shipping_date.repr_estimate.probability * 100)}%)</b>
            </EstimateInfo>
          )}
          {shipping_send_date && (
            <EstimateInfo className={Typography.BODY_14_REGULAR} line_height={null}>
              <SendDateText dangerouslySetInnerHTML={{ __html: shipping_send_date.send_date_text }}></SendDateText>
              {isFastShipping && type_text && <FastShippingBadge>{type_text}</FastShippingBadge>}
            </EstimateInfo>
          )}
          {isFastShipping && fast_guide_text && <GuideText text={fast_guide_text} />}
          {item_guide_text && <GuideText text={item_guide_text} />}
        </div>
      </ShippingInfoContent>
    </ShippingInfo>
  );
};

const ShippingInfo = styled.div`
  margin: 0 16px;
  padding: 14px 0 16px 0;
`;

const ShippingInfoContent = styled.div`
  display: flex;
  align-items: flex-start;
`;

const ShippingInfoTitle = styled.h2`
  min-width: 56px;
  margin-right: 8px;
  color: ${vars.v3Color.gray500};
  height: 20px;
  display: flex;
  align-items: center;
`;

const ShippingFeeInfo = styled.div<{ dotDivider: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  color: ${vars.v3Color.gray900};
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  height: 20px;

  b {
    color: ${vars.v3Color.pink400};
  }
`;

const Divider = styled.div`
  margin: 0 5px;
  position: relative;
  width: 1px;
  height: 12px;
  background: ${vars.v3Color.gray200};
`;

const FreeShippingAmount = styled.span`
  color: ${vars.v3Color.gray500};
  font-weight: 400;

  span {
    color: ${vars.v3Color.gray200};
  }
`;

const ShippingInfoList = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 0;
  padding: 0;
  margin: 0;
  border: 0;
`;

const EstimateInfo = styled.div<{ line_height?: string | null }>`
  display: flex;
  align-items: center;
  justify-items: center;
  flex-wrap: wrap;
  row-gap: 3px;
  margin: 8px 0 0 0;
  font-size: 14px;
  color: ${vars.v3Color.gray900};
  line-height: ${({ line_height }) => (line_height ? '140%' : '17px')};

  b {
    font-weight: 500;
  }
`;

const SendDateText = styled.span`
  margin: 0 3px 0 0;
`;

const FastShippingBadge = styled.span`
  display: inline-block;
  padding: 0 4px;
  height: 18px;
  font-size: 11px;
  font-weight: 500;
  color: ${vars.v3Color.gray600};
  text-align: center;
  border-radius: 2px;
  background: rgba(173, 181, 189, 0.1);
`;

const Area = styled.span`
  display: flex;
  align-items: center;
`;

const InfoContainer = styled.div`
  padding: 0 16px 40px 16px;
`;

const InfoTitle = styled.div`
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  margin-bottom: 12px;
`;
